import {useMemo} from 'react'
import {createUseStyles} from 'react-jss'
import {Breakpoints} from '../../constants/breakpoints'
import {DeactivatedEmployee} from "../../redux/types"
import ListItemCard from "../ListItemCard"
import Pagination from '../Pagination'
import {formatDateTimeString} from '../../util/date'
import {useTranslation} from 'react-i18next'

interface Props {
  employees: DeactivatedEmployee[]
  pageIndex: number
  pageSize: number
  onPageChange: (pageIndex: number) => void
}

const DeactivationHistoryList = ({employees, pageIndex, pageSize, onPageChange}: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'deactivations', 'employees'])

  const itemsOnCurrentPage = useMemo(() => {
    const sortedEmployees = [...employees].sort((a, b) => {
      const aTime = new Date(a.deactivationDate).getTime()
      const bTime = new Date(b.deactivationDate).getTime()
      return bTime - aTime
    })
    const firstPageIndex = (pageIndex - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    return sortedEmployees.slice(firstPageIndex, lastPageIndex)
  }, [employees, pageIndex, pageSize])

  const renderHeader = () => {
    return (
      <ListItemCard className={styles.listItemContainer}>
        <div className={styles.defaultCellContainer}>
          <strong>{t('id', {ns: 'common'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('idNumber', {ns: 'common'})}</strong>
        </div>
        <div className={styles.defaultCellContainer}>
          <strong>{t('name', {ns: 'common'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
          <strong>{t('employmentNumber', {ns: 'employees'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
          <strong>{t('deactivationDate', {ns: 'deactivations'})}</strong>
        </div>
        <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
          <strong>{t('deactivatedByPortalUserName', {ns: 'deactivations'})}</strong>
        </div>
      </ListItemCard>
    )
  }

  const renderListItems = () => {
    return itemsOnCurrentPage?.map((employee, index) => {
      return (
        <ListItemCard key={`deactivatedEmployee${index}`} className={styles.listItemContainer} index={index}>
          <div className={styles.defaultCellContainer}>
            {employee.externalId}
          </div>
          <div className={styles.defaultCellContainer}>
            {employee.identificationNumber}
          </div>
          <div className={styles.defaultCellContainer}>
            {employee.givenName} {employee.familyName}
          </div>
          <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
            {employee.employmentNumber || t('notProvided', {ns: 'common'})}
          </div>
          <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
            {formatDateTimeString(employee.deactivationDate)}
          </div>
          <div className={`${styles.defaultCellContainer} ${styles.smallHidden}`}>
            {employee.deactivatedByPortalUserName || t('notProvided', {ns: 'common'})}
          </div>
        </ListItemCard>
      )
    })
  }

  return (
    <div>
      {renderHeader()}
      {renderListItems()}
      <div className={styles.pagination}>
        <Pagination
          pageIndex={pageIndex}
          itemCount={employees.length}
          pageSize={pageSize}
          siblingCount={1}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  )
}

export default DeactivationHistoryList

const useStyles = createUseStyles({
  listItemContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 14
  },
  defaultCellContainer: {
    width: 170
  },
  smallHidden: {
    display: 'none',
    [`@media (min-width: ${Breakpoints.tablet}px)`]: {
      display: 'flex'
    },
  },
  pagination: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'center'
  }
})
